import React from 'react';
import StepIndicator from './StepIndicator';

/**
 * StatusTimeline Component
 * 
 * Status progress timeline with step indicators and progress visualization
 * Shows the progression through different transaction statuses
 * 
 * @param {string} status - Current status string
 * @param {boolean} isDarkMode - Dark mode state
 * @returns {JSX.Element} Status timeline component
 */
const StatusTimeline = ({ status, isDarkMode = false }) => {
  // Define the steps in order
  const steps = [
    { 
      key: 'onhold', 
      label: 'On Hold', 
      icon: (
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
          <path 
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
            stroke="currentColor" 
            strokeWidth="2"
          />
        </svg>
      ) 
    },
    { 
      key: 'processing', 
      label: 'Processing', 
      icon: (
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
          <path 
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
            stroke="currentColor" 
            strokeWidth="2"
          />
        </svg>
      ) 
    },
    { 
      key: 'nuwanis', 
      label: 'Ready', 
      icon: (
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
          <path 
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
            stroke="currentColor" 
            strokeWidth="2"
          />
        </svg>
      ) 
    },
    { 
      key: 'completed', 
      label: 'Complete', 
      icon: (
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
          <path 
            d="M5 13l4 4L19 7" 
            stroke="currentColor" 
            strokeWidth="2"
          />
        </svg>
      ) 
    },
  ];

  // Normalize status string
  const normalizedStatus = (status || '').toLowerCase().replace(/[-_ ]/g, '');
  
  // Find current step index
  const getCurrentStepIndex = () => {
    const currentIdx = steps.findIndex(step => {
      if (normalizedStatus === 'nuwanisuru' && step.key === 'processing') return true;
      if (normalizedStatus === 'nuwanis' && step.key === 'nuwanis') return true;
      if (normalizedStatus === 'complete' && step.key === 'completed') return true;
      return normalizedStatus === step.key;
    });
    return currentIdx === -1 ? 0 : currentIdx;
  };

  const currentIndex = getCurrentStepIndex();

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between">
        {steps.map((step, idx) => (
          <React.Fragment key={step.key}>
            <StepIndicator
              step={step}
              index={idx}
              currentIndex={currentIndex}
              isCompleted={idx < currentIndex}
              isCurrent={idx === currentIndex}
              isDarkMode={isDarkMode}
            />
            
            {/* Progress Line between steps */}
            {idx < steps.length - 1 && (
              <div className="flex-1 h-1 mx-2 bg-gray-200">
                <div 
                  className={`h-full ${idx < currentIndex ? 'bg-green-500' : ''}`}
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default StatusTimeline;
